// Pure dashboard aggregation (PRD §9.3). Kept out of React so it's unit-testable in node.
// Funnel counts DISTINCT simulations per stage (a sim may emit several events of a type).

export interface EventLite {
  id: string;
  type: string;
  occurred_at: string;
  simulation_id: string;
}

export const FUNNEL_STAGES = [
  'sent',
  'delivered',
  'opened',
  'clicked',
  'submitted_data',
  'reported',
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export type Funnel = Record<FunnelStage, number>;

export function funnelFromEvents(events: EventLite[]): Funnel {
  const sims: Record<string, Set<string>> = {};
  for (const e of events) (sims[e.type] ??= new Set()).add(e.simulation_id);
  const out = {} as Funnel;
  for (const stage of FUNNEL_STAGES) out[stage] = sims[stage]?.size ?? 0;
  return out;
}

export interface Counters {
  sent: number;
  clicked: number;
  reported: number;
  clickRate: number; // clicked / sent, 0..1
}

export function countersFromFunnel(f: Funnel): Counters {
  return {
    sent: f.sent,
    clicked: f.clicked,
    reported: f.reported,
    clickRate: f.sent > 0 ? f.clicked / f.sent : 0,
  };
}

export interface TimeBucket {
  t: number; // bucket start, epoch ms
  sent: number;
  opened: number;
  clicked: number;
}

/** Per-bucket event counts over time (default 1-minute buckets) for the time-series chart. */
export function timeSeries(events: EventLite[], bucketMs = 60_000): TimeBucket[] {
  const buckets = new Map<number, TimeBucket>();
  for (const e of events) {
    const ts = Date.parse(e.occurred_at);
    if (Number.isNaN(ts)) continue;
    const key = Math.floor(ts / bucketMs) * bucketMs;
    const b = buckets.get(key) ?? { t: key, sent: 0, opened: 0, clicked: 0 };
    if (e.type === 'sent') b.sent++;
    else if (e.type === 'opened') b.opened++;
    else if (e.type === 'clicked') b.clicked++;
    buckets.set(key, b);
  }
  return [...buckets.values()].sort((a, b) => a.t - b.t);
}

/** Most-recent events first, for the live feed. */
export function recentEvents(events: EventLite[], limit = 25): EventLite[] {
  return [...events]
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))
    .slice(0, limit);
}
